import { useEffect, useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import Landing from "./pages/Landing";
import AccessDenied from "./pages/AccessDenied";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AiChatBubble } from "./components/AiChatBubble";

// Lazy-load heavy pages — Dashboard and Settings are large bundles
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Settings = lazy(() => import("./pages/Settings"));
const PublicOrgChart = lazy(() => import("./pages/PublicOrgChart"));
const GestionVacances = lazy(() => import("./portal/GestionVacances"));

// Runtime domain detection - works in any deployment environment
const getCurrentDomain = () => {
  // In deployed environments (production/preview), use window.location.origin
  // The Kubernetes ingress routes /api calls to the backend
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    // Only use REACT_APP_BACKEND_URL for localhost (testing agent)
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return process.env.REACT_APP_BACKEND_URL || window.location.origin;
    }
    // For all deployed environments, use current origin
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getCurrentDomain();
const API = `${BACKEND_URL}/api`;

export const axiosInstance = axios.create({
  baseURL: API,
  withCredentials: true,
});

// ── Pydantic V2 ValidationError normaliser ─────────────────────────────
// FastAPI returns `detail` as an ARRAY of {type, loc, msg, input, url}
// objects when a Pydantic validation fails (HTTP 422). Most error handlers
// in the codebase do `toast.error(err.response.data.detail || "fallback")`,
// which crashes React with "Objects are not valid as a React child" when
// detail is an array because sonner tries to render it inline.
//
// This single response interceptor rewrites `detail` into a plain string
// BEFORE any handler sees the error — so all 20+ `toast.error(...detail)`
// callsites keep working with zero code churn and we get human-readable
// messages instead of `[object Object]` or a React crash.
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const detail = error?.response?.data?.detail;
    if (Array.isArray(detail)) {
      // Pydantic V2 ValidationError shape — flatten {loc, msg} pairs into
      // "field.path: human message" lines. Limit to 4 to keep toasts short.
      const lines = detail
        .slice(0, 4)
        .map((d) => {
          if (!d || typeof d !== "object") return String(d);
          const loc = Array.isArray(d.loc)
            ? d.loc.filter((x) => x !== "body").join(".")
            : "";
          const msg = d.msg || d.type || "validation error";
          return loc ? `${loc}: ${msg}` : msg;
        });
      const more = detail.length > 4 ? ` (+${detail.length - 4} more)` : "";
      error.response.data.detail = lines.join(" • ") + more;
    } else if (detail && typeof detail === "object") {
      // Single-object detail (rare; e.g. custom HTTPException with dict).
      try {
        error.response.data.detail = JSON.stringify(detail);
      } catch {
        error.response.data.detail = "Erreur inattendue";
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axiosInstance.get("/auth/me");
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-black"><div className="text-white text-xl">Loading...</div></div>}>
          <Routes>
            <Route
              path="/"
              element={
                user ? (
                  user.is_portal_only && !user.is_admin ? (
                    <Navigate to="/gestionvacances" replace />
                  ) : user.has_access ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <Navigate to="/access-denied" replace />
                  )
                ) : (
                  <Landing onAuthSuccess={() => checkAuth()} />
                )
              }
            />
            <Route
              path="/dashboard"
              element={
                user ? (
                  user.is_portal_only && !user.is_admin ? (
                    <Navigate to="/gestionvacances" replace />
                  ) : user.has_access ? (
                    <Dashboard user={user} onDéconnexion={() => setUser(null)} />
                  ) : (
                    <Navigate to="/access-denied" replace />
                  )
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/settings"
              element={
                user ? (
                  user.is_portal_only && !user.is_admin ? (
                    <Navigate to="/gestionvacances" replace />
                  ) : user.has_access ? (
                    <Settings user={user} onLogout={() => setUser(null)} onUserUpdate={checkAuth} />
                  ) : (
                    <Navigate to="/access-denied" replace />
                  )
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/gestionvacances"
              element={
                user ? (
                  <GestionVacances user={user} onLogout={() => setUser(null)} />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/access-denied"
              element={
                user ? (
                  <AccessDenied user={user} onLogout={() => setUser(null)} />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/orgchart/public"
              element={<PublicOrgChart />}
            />
          </Routes>
          </Suspense>
          {user?.is_admin && user?.has_access && <AiChatBubble />}
        </BrowserRouter>
        <Toaster />
      </div>
    </ThemeProvider>
  );
}

export default App;
